// ============================================================================
// DEMONSTRATORS TAB
// Browse the global demonstrator template catalog and download a starter zip.
// Read-only — catalog management happens in the admin area, not here.
// ============================================================================

import React, { useEffect, useState, useCallback } from 'react';
import { Rocket, Download, AlertCircle } from 'lucide-react';
import { listDemoTemplates, getDemoTemplate, DemoTemplate } from '../../services/demoTemplatesService';
import { EmptyState } from '../EmptyState';

type CategoryFilter = 'all' | DemoTemplate['category'];

const CATEGORY_LABELS: Record<DemoTemplate['category'], string> = {
  'agentic-workflow': 'Agentic Workflow',
  'customer-portal': 'Customer Portal',
  'report': 'Report',
  'other': 'Other',
};

const FILTER_OPTIONS: { value: CategoryFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'agentic-workflow', label: 'Agentic Workflow' },
  { value: 'customer-portal', label: 'Customer Portal' },
  { value: 'report', label: 'Report' },
  { value: 'other', label: 'Other' },
];

export function DemoTemplatesTab() {
  const [templates, setTemplates] = useState<DemoTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await listDemoTemplates();
      setTemplates(result.filter((t) => t.active));
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to load demonstrator templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDownload = async (templateId: string) => {
    try {
      setDownloadingId(templateId);
      const { downloadUrl } = await getDemoTemplate(templateId);
      if (downloadUrl) window.open(downloadUrl, '_blank');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to get download link');
    } finally {
      setDownloadingId(null);
    }
  };

  const filtered = categoryFilter === 'all'
    ? templates
    : templates.filter((t) => t.category === categoryFilter);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden w-fit">
        {FILTER_OPTIONS.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setCategoryFilter(opt.value)}
            className={`px-4 py-2 text-xs font-semibold transition-all ${
              categoryFilter === opt.value ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-20 text-[#64748b]">
          <div className="text-sm font-medium">Loading demonstrator templates...</div>
        </div>
      ) : error ? (
        <div className="rounded-xl bg-red-50 border border-red-200 p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-medium text-red-700">{error}</p>
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Rocket}
          title={templates.length === 0 ? 'No demonstrator templates yet' : 'No templates in this category'}
          description={
            templates.length === 0
              ? 'Ask a template admin to publish a starter repo to the catalog.'
              : 'Try a different category filter.'
          }
        />
      ) : (
        <div className="flex flex-col gap-3">
          {filtered.map((template) => (
            <div
              key={template.templateId}
              className="border border-[#e2e8f0] rounded-xl overflow-hidden bg-white p-5 flex items-start justify-between gap-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex items-start gap-4">
                <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary flex-shrink-0">
                  <Rocket size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold text-navy">{template.name}</h3>
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-600">
                      {CATEGORY_LABELS[template.category]}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">{template.description}</p>
                  <p className="text-xs text-slate-400 mt-2">Tech stack: {template.techStack}</p>
                  {template.requiredEnvVars.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {template.requiredEnvVars.map((envVar) => (
                        <span key={envVar} className="px-2 py-0.5 rounded-full text-[10px] font-mono bg-slate-50 border border-slate-200 text-slate-500">
                          {envVar}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDownload(template.templateId)}
                disabled={downloadingId === template.templateId}
                className="flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-60 flex-shrink-0"
              >
                <Download size={16} />
                {downloadingId === template.templateId ? 'Preparing…' : 'Download'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
